
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Settings, BrainCircuit, BookText, MessageCircle, Sparkles, Wrench, Key } from "lucide-react";
import { AgentConfiguration } from "@/components/candidates/types/agent";
import { GeneralConfigTab } from "./GeneralConfigTab";
import { ModelConfigTab } from "./ModelConfigTab";
import { KnowledgeConfigTab } from "./KnowledgeConfigTab";
import { BehaviorConfigTab } from "./BehaviorConfigTab";
import { ToolsConfigTab } from "./ToolsConfigTab";
import { ApiKeyManagementTab } from "./ApiKeyManagementTab";

type AgentConfigurationPanelProps = {
  selectedAgent: AgentConfiguration;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onConfigUpdate: (config: Partial<AgentConfiguration>) => void;
};

export const AgentConfigurationPanel = ({
  selectedAgent,
  activeTab,
  onTabChange,
  onConfigUpdate
}: AgentConfigurationPanelProps) => {
  return (
    <Card className="md:col-span-3">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <span>{selectedAgent.name} Configuration</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor="agent-status" className="text-sm">Enabled</Label>
            <Switch
              id="agent-status"
              checked={selectedAgent.isEnabled}
              onCheckedChange={(checked) => onConfigUpdate({ isEnabled: checked })}
            />
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={onTabChange} className="space-y-4">
          <TabsList className="grid grid-cols-6">
            <TabsTrigger value="general" className="flex items-center gap-1">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="model" className="flex items-center gap-1">
              <BrainCircuit className="h-4 w-4" />
              <span className="hidden sm:inline">Model</span>
            </TabsTrigger>
            <TabsTrigger value="knowledge" className="flex items-center gap-1">
              <BookText className="h-4 w-4" />
              <span className="hidden sm:inline">Knowledge</span>
            </TabsTrigger>
            <TabsTrigger value="behavior" className="flex items-center gap-1">
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Behavior</span>
            </TabsTrigger>
            <TabsTrigger value="tools" className="flex items-center gap-1">
              <Wrench className="h-4 w-4" />
              <span className="hidden sm:inline">Tools</span>
            </TabsTrigger>
            <TabsTrigger value="api-keys" className="flex items-center gap-1">
              <Key className="h-4 w-4" />
              <span className="hidden sm:inline">API Keys</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="general">
            <GeneralConfigTab selectedAgent={selectedAgent} updateAgentConfig={onConfigUpdate} />
          </TabsContent>
          
          <TabsContent value="model">
            <ModelConfigTab selectedAgent={selectedAgent} updateAgentConfig={onConfigUpdate} />
          </TabsContent>
          
          <TabsContent value="knowledge">
            <KnowledgeConfigTab selectedAgent={selectedAgent} updateAgentConfig={onConfigUpdate} />
          </TabsContent>
          
          <TabsContent value="behavior">
            <BehaviorConfigTab selectedAgent={selectedAgent} updateAgentConfig={onConfigUpdate} />
          </TabsContent>
          
          <TabsContent value="tools">
            <ToolsConfigTab selectedAgent={selectedAgent} updateAgentConfig={onConfigUpdate} />
          </TabsContent>
          
          <TabsContent value="api-keys">
            <ApiKeyManagementTab selectedAgent={selectedAgent} updateAgentConfig={onConfigUpdate} />
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end mt-6">
          <Button className="bg-invenio-purple hover:bg-invenio-dark-purple">
            <Sparkles className="mr-2 h-4 w-4" />
            Save Configuration
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
